import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureIframeMountScheduler,
  iframeMountScheduler,
  type IframeMountHandle,
} from "../../../lib/iframeMountScheduler";

/** How an HTML card decides when its iframe may mount. */
export type IframeMountPolicy = "eager" | "progressive" | "capture" | "poster";

export interface UseIframeMountAdmissionParams {
  /** `"progressive"` paces the mount through the shared scheduler;
   *  `"capture"` through the screenshot page's own, which is faster and
   *  stops after a fixed number of documents; `"eager"` admits immediately;
   *  `"poster"` never admits (a whole-canvas capture shows posters only). */
  policy: IframeMountPolicy;
  /** Card host element the scheduler observes for viewport proximity. */
  hostRef: React.RefObject<Element | null>;
  /** The card's document is prepared and could mount right now (i.e.
   *  `displayDoc !== null`). Progressive cards enter the scheduler only once
   *  ready, so a granted slot always paces a real iframe mount and the load
   *  event is what returns it. Registering earlier would burn slots on cards
   *  whose fetch is still in flight — their slots would leak back via the
   *  fallback timeout, and the mounts would then cluster un-paced whenever
   *  the fetches resolve. */
  ready?: boolean;
  /** This card shows a poster while it waits, so it loses a contested slot to
   *  one that would show nothing. Read once, when the card registers: a
   *  poster that arrives later does not re-rank the queue. */
  hasPoster?: boolean;
}

export interface UseIframeMountAdmissionResult {
  /** Render the iframe only when true. */
  admitted: boolean;
  /** Wire to the iframe's `onLoad` — frees the scheduler's concurrency slot.
   *  Safe (no-op) under the eager policy. */
  onIframeLoad: () => void;
}

/**
 * Card-side face of the iframe mount scheduler (see iframeMountScheduler.ts
 * for why mounts are paced at all). Admission is one-way: once granted it is
 * never revoked, so an admitted card keeps its live iframe on scroll-away.
 * Under `"capture"` a card past the page's document cap is never granted one
 * at all and stays on its poster, which is what the screenshot wants.
 */
export function useIframeMountAdmission({
  policy,
  hostRef,
  ready = true,
  hasPoster = false,
}: UseIframeMountAdmissionParams): UseIframeMountAdmissionResult {
  const scheduled = policy !== "eager";
  const neverAdmits = policy === "poster";
  const [admitted, setAdmitted] = useState(false);
  const handleRef = useRef<IframeMountHandle | null>(null);
  const hasPosterRef = useRef(hasPoster);
  hasPosterRef.current = hasPoster;

  useEffect(() => {
    if (!scheduled || neverAdmits || !ready) return;
    const el = hostRef.current;
    if (!el) {
      // Fail OPEN. A ref object's identity never changes, so this effect has
      // no way to re-run when the element attaches later — a closed gate here
      // would strand the card on its placeholder forever, with no error and
      // no timeout. An unpaced mount beats a silently blank card.
      setAdmitted(true);
      return;
    }
    const scheduler =
      policy === "capture" ? captureIframeMountScheduler : iframeMountScheduler;
    const handle = scheduler.register(el, () => setAdmitted(true), {
      hasPoster: hasPosterRef.current,
    });
    handleRef.current = handle;
    return () => {
      handleRef.current = null;
      handle.release();
    };
    // `hasPoster` is deliberately not a dependency: it is read once, at
    // registration. Re-running on a poster that arrives late would release a
    // granted slot while `admitted` stays true, and the re-registration would
    // then take a second slot that nothing returns.
  }, [policy, scheduled, neverAdmits, ready, hostRef]);

  const onIframeLoad = useCallback(() => {
    handleRef.current?.loaded();
  }, []);

  // Eager always renders. (Admission state is one-way — nothing un-sets it —
  // so a granted slot survives everything short of unmount.)
  return { admitted: admitted || !scheduled, onIframeLoad };
}
